import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {ClientCredentials} from '../_models/clientCredentials';
import {Item} from '../_models/Item';
import {Observable, of} from 'rxjs';
import {retry, shareReplay} from 'rxjs/operators';
import {AESEncryptDecryptService} from './aesencrypt-decrypt.service';
import {MerchantSettings} from '../_models/merchantSettings';
import {ServiceCharge} from '../_models/serviceCharge';
import {Category} from '../_models/category';
import {AuthenticationService} from './authentication.service';
import {UtilsService} from "./utils.service";

@Injectable({providedIn: 'root'})
export class CloverApiService {
  cloverItemsWithImages: Observable<Item[]>;
  mostSoldItems: Observable<any>;
  mostProfitableItems: Observable<any>;
  cloverItems: Observable<Item[]>;
  itemsWithImages: Observable<Item[]>;
  cloverCategoriesWithImagesAndItems: Observable<any>;
  cloverCategoriesWithImages: Observable<Category[]>;
  modifierGroups: Observable<any[]>;
  CACHE_SIZE = 1;

  constructor(private http: HttpClient, private _AESEncryptDecryptService: AESEncryptDecryptService, private utilsService: UtilsService) {
  }

  private isAuthenticated(): boolean {
    return !!this.utilsService.getCurrentUser();
  }

  SaveClientCredentials(body: ClientCredentials) {
    return this.http.post<any>(`${environment.apiUrl}/clientCredentials/saveClientCredentials`, body);
  }


  getAllCategories() {
    const body = {};
    if (!this.cloverCategoriesWithImages) {
      this.cloverCategoriesWithImages = this.http.get<Category[]>(`${environment.apiUrl}/cloverApi/categoriesWithImages`)
        .pipe(shareReplay(this.CACHE_SIZE), retry(3));
    }
    return this.cloverCategoriesWithImages;
  }

  getMostSoldItems() {
    const body = {};

    if (!this.isAuthenticated()) {
      console.warn('User not authenticated, skipping request:');
      return of(null as unknown);
    }

    if (!this.mostSoldItems) {
      this.mostSoldItems = this.http.post<any>(`${environment.apiUrl}/cloverApi/getMostSoldItems`, body)
        .pipe(shareReplay(this.CACHE_SIZE), retry(3));
    }
    return this.mostSoldItems;
  }

  getMostProfitableItems() {
    const body = {};

    if (!this.isAuthenticated()) {
      console.warn('User not authenticated, skipping request:');
      return of(null as unknown);
    }

    if (!this.mostProfitableItems) {
      this.mostProfitableItems = this.http.post<any>(`${environment.apiUrl}/cloverApi/getMostProfitableItems`, body)
        .pipe(shareReplay(this.CACHE_SIZE), retry(3));
    }
    return this.mostProfitableItems;
  }

  getCategoriesWithImagesAndItems() {
    const body = {};

    if (!this.isAuthenticated()) {
      console.warn('User not authenticated, skipping request:');
      return of(null as unknown);
    }

    this.cloverCategoriesWithImagesAndItems = this.http.post<[Category[], Item[]]>(`${environment.apiUrl}/cloverApi/categoriesWithImagesAndItems`, body)
      .pipe(shareReplay(this.CACHE_SIZE), retry(3));
    return this.cloverCategoriesWithImagesAndItems;
  }


  getAllItems(limit, offset) {
    const body = {};

    if (!this.isAuthenticated()) {
      console.warn('User not authenticated, skipping request:');
      return of(null as unknown);
    }

    // `offset` is a 0-indexed skip, so a falsy check would silently drop the
    // first page (offset:0). Use an explicit `!= null` check on both args
    // so paging at the start of the catalog still appends the limit/offset
    // pair to the URL. The HTTP client recreates the observable per call
    // when paging — caching is only used for the unparameterised "give me
    // everything" call site.
    const isPaged = limit != null && offset != null;
    if (!this.cloverItemsWithImages || isPaged) {
      let url = `${environment.apiUrl}/cloverApi/itemsWithImages`;

      if (isPaged) {
        url = url + '?limit=' + limit + '&offset=' + offset;
      }
      this.cloverItemsWithImages = this.http.get<Item[]>(url)
        .pipe(shareReplay(this.CACHE_SIZE), retry(3));
    }
    return this.cloverItemsWithImages;
  }

  getAllItemsWithoutImages() {
    const body = {};

    if (!this.isAuthenticated()) {
      console.warn('User not authenticated, skipping request:');
      return of(null as unknown);
    }

    if (!this.cloverItems) {
      this.cloverItems = this.http.post<Item[]>(`${environment.apiUrl}/cloverApi/items`, body)
        .pipe(shareReplay(this.CACHE_SIZE), retry(3));
    }
    return this.cloverItems;
  }

  getAllItemsSomeWithImages() {
    const body = {};
    return this.http.post<any>(`${environment.apiUrl}/cloverApi/itemsSomeWithImages`, body)
      .pipe(shareReplay(this.CACHE_SIZE));
  }


  getAllItemsByCategoryId(id: string) {
    return this.http.get<any>(`${environment.apiUrl}/cloverApi/itemsByCategoryWithImages/` + id);
  }

  getItemByItemId(id: string) {


    const body = {
      'item_id': id
    };
    return this.http.post<any>(`${environment.apiUrl}/cloverApi/itemByItemId`, body);
  }

  getAllEmployees() {

    return this.http.get<any[]>(`${environment.apiUrl}/cloverApi/getAllEmployees`);
  }

  getAllCustomers() {
    return this.http.get<any[]>(`${environment.apiUrl}/cloverApi/getAllCustomers`);
  }

  getAllCategoriesByItemId(id: string) {
    const body = {
      'item_id': id
    };
    return this.http.post<any>(`${environment.apiUrl}/cloverApi/categoriesByItems`, body);
  }

  getDefaultTaxRate() {
    // console.log('in CloverApiService getAllCategoriesByItemID'); //*MES*

    const body = {};
    return this.http.post<any>(`${environment.apiUrl}/cloverApi/getDefaultTaxRate`, body);
  }

  /** Returns ALL tax rates for the merchant (Clover API → local DB fallback). */
  fetchAllTaxRates() {
    return this.http.post<any[]>(`${environment.apiUrl}/cloverApi/getAllTaxRates`, {});
  }

  getDefaultServiceCharge() {
    return this.http.get<ServiceCharge>(`${environment.apiUrl}/cloverApi/getDefaultServiceCharge`);
  }

  getMerchantSetttings() {
    return this.http.get<MerchantSettings>(`${environment.apiUrl}/cloverApi/merchantSettings`);
  }


  getModifierGroupsByItemId(id: string) {
    if (!this.isAuthenticated()) {
      console.warn('User not authenticated, skipping request:');
      return of(null as unknown);
    }
    return this.http.get<any>(`${environment.apiUrl}/cloverApi/modifierGroupsByItemId?id=` + id);
  }

  getModifierGroups() {

    if (!this.isAuthenticated()) {
      console.warn('User not authenticated, skipping request:');
      return of(null as unknown);
    }

    if (!this.modifierGroups) {
      this.modifierGroups = this.http.post<any[]>(`${environment.apiUrl}/cloverApi/modifierGroups`, {})
        .pipe(shareReplay(this.CACHE_SIZE), retry(3));
    }
    return this.modifierGroups;
  }

  /** Non-cached fetch for admin screens that need fresh data after CRUD. */
  fetchModifierGroupsFresh() {
    return this.http.post<any[]>(`${environment.apiUrl}/cloverApi/modifierGroups`, {});
  }

  initKioskOnDevice(deviceId: string, deviceSerial: string, firePrint?: boolean) {
    const body = {
      'firePrint': firePrint ? firePrint : '',
      'deviceSerial': deviceSerial,
      'deviceId': deviceId,
    };
    return this.http.post<any>(`${environment.apiUrl}/cloverOrders/initKioskOnDevice`, body);
  }

  disableKioskOnDevice(deviceId: string, deviceSerial: string) {
    const body = {
      'deviceSerial': deviceSerial,
      'deviceId': deviceId,
    };
    return this.http.post<any>(`${environment.apiUrl}/cloverOrders/disableKioskOnDevice`, body);
  }

  getAllModifiersByGroupId(id: string) {
    const body = {
      'modifiersGroupId': id
    };
    return this.http.post<any>(`${environment.apiUrl}/cloverApi/modifiersByGroupId`, body);
  }

  getAllModifiers() {
    return this.http.get<any>(`${environment.apiUrl}/cloverApi/getAllModifiers`);
  }

  updateSortOrder(payload: { categories?: {id: string, sortOrder: number}[], items?: {id: string, sortOrder: number}[] }) {
    return this.http.put<any>(`${environment.apiUrl}/inventoryManagement/sortOrder`, payload);
  }

  invalidateMenuCache(): void {
    this.cloverCategoriesWithImages = null;
    this.cloverCategoriesWithImagesAndItems = null;
    this.cloverItemsWithImages = null;
    this.cloverItems = null;
  }

  generateDashboardAnalytics(data: any) {
    return this.http.post<any>(`${environment.apiUrl}/cloverApi/generateAnalytics`, data);
  }

  updateItemPrice(itemId: string, newPrice: number) {
    return this.http.post<any>(`${environment.apiUrl}/cloverApi/updateItemPrice`, {itemId, price: newPrice});
  }
}
